/**
 * SEO component with Gatsby Head API
 *
 * See: https://www.gatsbyjs.com/docs/how-to/adding-common-features/adding-seo-component/
 */

import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';

export const SEO = ({ 
  title, 
  description, 
  pathname, 
  keywords, 
  imageUrl, 
  staticImage, 
  ogType,
  structuredData = [],
  getStructuredData
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          image
          siteUrl
        }
      }
    }
  `);

  const {title: defaultTitle, description: defaultDescription, image: defaultImage, siteUrl, author} = site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: imageUrl || (staticImage ? `${siteUrl}${staticImage}` : `${siteUrl}${defaultImage}`),
    url: `${siteUrl}${pathname || ``}`,
    keywords: keywords,
    type: ogType || `website`,
  }

  // Base OneShift organization data that should be present on all pages
  const baseOneShiftOrganizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "OneShift by Carousell",
    "url": siteUrl,
    "logo": `${siteUrl}/images/oneshift-logo-og.png`,
    "image": `${siteUrl}/images/oneshift-logo-og.png`,
    "telephone": "+6565335857",
    "description": "Singapore's trusted automotive portal for car reviews, news, and prices",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5 Toa Payoh West, #01-00, Motorway Building, Singapore 318877",
      "addressLocality": "Toa Payoh",
      "addressRegion": "Singapore",
      "postalCode": "319255",
      "addressCountry": "SG"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "1.3403",
      "longitude": "103.8592"
    },
    "sameAs": [
      "https://www.facebook.com/oneshiftsg/",
      "https://www.instagram.com/oneshift/",
      "https://www.tiktok.com/@oneshiftbycarousell/"
    ]
  };

  // If getStructuredData is provided, use it to generate structured data with siteUrl
  const finalStructuredData = [
    baseOneShiftOrganizationData,
    ...(getStructuredData ? getStructuredData(siteUrl) : structuredData)
  ];

  return (
    <>
      <html lang="en" />
      <link href={seo.url} rel="canonical" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="author" content={author} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}

      <meta property="og:title" content={seo.title} />
      <meta property="og:type" content={seo.type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:locale" content="en_SG" />
      <meta property="og:description" content={seo.description} />
      <meta property="og:site_name" content="OneShift" />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.title} />
      <meta property="og:image:type" content="image/jpeg" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={author} />
      <meta property="twitter:domain" content={siteUrl} />
      <meta property="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.title} />

      <meta property="fb:app_id" content="349913042127" />
      <meta property="article:publisher" content="https://www.facebook.com/oneshiftsg/" />

      <meta name="geo.region" content="SG" />
      <meta name="geo.placename" content="OneShift by Carousell" />
      <meta name="geo.position" content="1.3403;103.8592" />
      <meta name="ICBM" content="1.3403, 103.8592" />

      {finalStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </>
  );
}

export default SEO;